import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from '@mui/material';
import { MdExpandMore, MdExpandLess } from 'react-icons/md';
import theme, { conditionBg } from '../theme';
import api from '../../utils/api';

const COLORS = theme.colors;

const TYPE_LABEL = {
  partial_cycle: 'Partial cycle',
  overdue_room: 'Overdue room',
  residual_positive: 'Residual positive',
  motion_abort: 'Motion abort',
  manual_wipe_required: 'Manual wipe required',
  incomplete_record: 'Incomplete record (no manual clean)',
};

/**
 * @param {{
 *   exceptions: object[],
 *   loading?: boolean,
 *   error?: string,
 *   email: string,
 *   onChanged?: () => void,
 *   onOpenPack?: (historyId: string) => void,
 *   readOnly?: boolean,
 * }} props
 */
export default function ExceptionQueuePanel({
  exceptions = [],
  loading,
  error,
  email,
  onChanged,
  onOpenPack,
  readOnly = false,
}) {
  const [filter, setFilter] = useState('open');
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolutionNote, setResolutionNote] = useState('');
  const [busyId, setBusyId] = useState('');
  const [expanded, setExpanded] = useState(false);

  const filtered = exceptions.filter((ex) => {
    if (filter === 'all') return true;
    if (filter === 'open') return ex.status === 'open' || ex.status === 'acknowledged';
    return ex.status === filter;
  });

  const patchStatus = async (id, status, note) => {
    setBusyId(id);
    try {
      await api.patch(
        `/client/reports/exceptions/${encodeURIComponent(id)}?email=${encodeURIComponent(email)}`,
        { status, resolutionNote: note || '', email }
      );
      onChanged?.();
    } catch (e) {
      console.error('patch exception', e);
    } finally {
      setBusyId('');
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: `${theme.radius.lg}px`,
        border: `1px solid ${COLORS.borderDefault}`,
        bgcolor: '#fff',
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          mb: expanded ? 2 : 0,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary }}>
            Exception queue
          </Typography>
          <Typography sx={{ fontSize: 13, color: COLORS.textSecondary, mt: 0.5 }}>
            Disinfection and environmental-hygiene gaps only: partial cycles, overdue rooms, motion
            aborts, post-UVC residuals, manual wipe gaps, and incomplete records (UV without manual
            clean). Not a hospital-wide CAPA, policy, or quality-management workflow.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {expanded ? (
            <TextField
              select
              size="small"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="acknowledged">Acknowledged</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </TextField>
          ) : null}
          <Button
            size="small"
            variant="contained"
            endIcon={expanded ? <MdExpandLess size={18} /> : <MdExpandMore size={18} />}
            onClick={() => setExpanded((v) => !v)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: `${theme.radius.md}px`,
              flexShrink: 0,
              background: theme.gradients.primary,
              color: '#fff',
              boxShadow: 'none',
              '&:hover': {
                background: theme.gradients.primaryHover,
                boxShadow: 'none',
              },
            }}
          >
            {expanded ? 'Close' : 'Open'}
          </Button>
        </Box>
      </Box>

      {expanded ? (
        <>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : filtered.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: COLORS.textSecondary, py: 2 }}>
              No exceptions in this view.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {filtered.map((ex) => (
                <Box
                  key={ex.id}
                  sx={{
                    p: 1.75,
                    borderRadius: `${theme.radius.md}px`,
                    border: `1px solid ${COLORS.borderDefault}`,
                    bgcolor: conditionBg(ex.severity === 'high' ? 'danger' : 'warning'),
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.textPrimary }}>
                      {ex.title}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                      <Chip size="small" label={TYPE_LABEL[ex.type] || ex.type} />
                      <Chip size="small" label={ex.status} variant="outlined" />
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: 12, color: COLORS.textSecondary, mt: 0.75 }}>
                    {ex.detail}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: COLORS.textSecondary, mt: 0.5 }}>
                    {ex.occurredAt ? new Date(ex.occurredAt).toLocaleString('en-GB') : '—'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1.25, flexWrap: 'wrap' }}>
                    {ex.historyId ? (
                      <Button
                        size="small"
                        sx={{ textTransform: 'none' }}
                        onClick={() => onOpenPack?.(ex.historyId)}
                      >
                        Open pack
                      </Button>
                    ) : null}
                    {ex.status === 'open' && !readOnly ? (
                      <Button
                        size="small"
                        disabled={busyId === ex.id}
                        sx={{ textTransform: 'none' }}
                        onClick={() => patchStatus(ex.id, 'acknowledged')}
                      >
                        Acknowledge
                      </Button>
                    ) : null}
                    {ex.status !== 'resolved' && !readOnly ? (
                      <Button
                        size="small"
                        disabled={busyId === ex.id}
                        sx={{ textTransform: 'none' }}
                        onClick={() => {
                          setResolveTarget(ex);
                          setResolutionNote('');
                        }}
                      >
                        Resolve
                      </Button>
                    ) : null}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </>
      ) : null}

      <Dialog open={Boolean(resolveTarget)} onClose={() => setResolveTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Resolve exception</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: COLORS.textSecondary, mb: 1.5 }}>
            {resolveTarget?.title}
          </Typography>
          <TextField
            label="Resolution note"
            fullWidth
            multiline
            minRows={2}
            value={resolutionNote}
            onChange={(e) => setResolutionNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveTarget(null)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ textTransform: 'none' }}
            disabled={!resolveTarget || busyId === resolveTarget?.id}
            onClick={async () => {
              await patchStatus(resolveTarget.id, 'resolved', resolutionNote);
              setResolveTarget(null);
            }}
          >
            Mark resolved
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
